import { useCallback, useEffect, useRef, useState, type DependencyList, type RefObject } from "react"
import { zoomedBarCount } from "@/lib/chart/timeframes"

export interface ChartViewport {
  start: number
  end: number
}

export interface ChartSize {
  width: number
  height: number
  /** Device pixel ratio; changes when the window moves to another monitor or zooms. */
  dpr: number
}

interface UseChartViewportOptions {
  length: number
  defaultWindow?: number
  minWindow?: number
  leftPadding?: number
  rightPadding?: number
  minHeight?: number
  maxHeight?: number
  heightRatio?: number
  /**
   * The viewport resets to the default window whenever this value changes (new data,
   * new range). Other length changes, such as toggling a series, keep the current zoom.
   * Defaults to `length`.
   */
  resetKey?: unknown
  /**
   * Point positions (0–1 of the plot width) for the visible window when points aren't evenly
   * spaced; hover then picks the nearest point. Omit for even spacing.
   */
  pointRatios?: (viewport: ChartViewport) => number[] | null
}

/** Pointer position in CSS pixels relative to the canvas, plus the hovered timeline index. */
interface ChartHover {
  index: number | null
  x: number | null
  y: number | null
}

const EMPTY_HOVER: ChartHover = { index: null, x: null, y: null }

export function useChartViewport({
  length,
  defaultWindow = 180,
  minWindow = 12,
  leftPadding = 12,
  rightPadding = 70,
  minHeight = 280,
  maxHeight = 520,
  heightRatio = 0.5,
  resetKey = length,
  pointRatios,
}: UseChartViewportOptions) {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const dragRef = useRef<{ x: number; viewport: ChartViewport } | null>(null)
  // Plain wheel events only zoom once the chart has been clicked, so scrolling the page
  // past a chart doesn't get trapped. Ctrl/⌘ + wheel (and trackpad pinch) always zoom.
  const wheelZoomRef = useRef(false)
  const previousLengthRef = useRef(length)
  const [size, setSize] = useState<ChartSize>({ width: 800, height: minHeight, dpr: currentDpr() })
  const [viewport, setViewport] = useState<ChartViewport>(() =>
    initialViewport(length, defaultWindow),
  )
  const [hover, setHover] = useState<ChartHover>(EMPTY_HOVER)

  const indexFromPointer = useCallback(
    (clientX: number, current: ChartViewport = viewport): number | null => {
      const canvas = canvasRef.current
      if (!canvas || current.end <= current.start) return null
      const bounds = canvas.getBoundingClientRect()
      const plotX = clientX - bounds.left - leftPadding
      const plotWidth = bounds.width - leftPadding - rightPadding
      if (plotX < 0 || plotX > plotWidth) return null
      const ratios = pointRatios?.(current)
      if (ratios && ratios.length > 0) {
        const ratio = plotX / plotWidth
        let nearest = 0
        ratios.forEach((value, index) => {
          if (Math.abs(value - ratio) < Math.abs(ratios[nearest] - ratio)) nearest = index
        })
        return current.start + nearest
      }
      const local = Math.min(
        current.end - current.start - 1,
        Math.max(0, Math.floor((plotX / plotWidth) * (current.end - current.start))),
      )
      return current.start + local
    },
    [leftPadding, pointRatios, rightPadding, viewport],
  )

  const zoomStateRef = useRef({ length, minWindow, viewport, indexFromPointer })
  zoomStateRef.current = { length, minWindow, viewport, indexFromPointer }

  useEffect(() => {
    const previous = previousLengthRef.current
    previousLengthRef.current = length
    if (previous !== length) setViewport((current) => clampViewport(current, previous, length))
  }, [length])

  // Declared after the clamp effect so a reset wins when both fire in the same commit.
  // Only resetKey re-frames the chart; length/defaultWindow are read at reset time.
  useEffect(() => {
    setViewport(initialViewport(length, defaultWindow))
  }, [resetKey])

  useEffect(() => {
    const element = containerRef.current
    if (!element) return
    const observer = new ResizeObserver(([entry]) => {
      const width = Math.max(1, Math.floor(entry.contentRect.width))
      setSize({
        width,
        height: Math.max(minHeight, Math.min(maxHeight, Math.floor(width * heightRatio))),
        dpr: currentDpr(),
      })
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [heightRatio, maxHeight, minHeight])

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return
    const query = window.matchMedia(`(resolution: ${size.dpr}dppx)`)
    const onChange = (): void => setSize((current) => ({ ...current, dpr: currentDpr() }))
    query.addEventListener?.("change", onChange)
    return () => query.removeEventListener?.("change", onChange)
  }, [size.dpr])

  useEffect(() => {
    const element = containerRef.current
    if (!element) return

    const onWheel = (event: WheelEvent): void => {
      if (!wheelZoomRef.current && !event.ctrlKey && !event.metaKey) return
      event.preventDefault()
      event.stopPropagation()
      const state = zoomStateRef.current
      if (state.length === 0 || event.deltaY === 0) return
      const current = state.viewport
      const anchor = state.indexFromPointer(event.clientX, current) ?? current.end - 1
      const currentCount = current.end - current.start
      const clampedCount = zoomedBarCount(currentCount, event.deltaY, {
        minWindow: state.minWindow,
        length: state.length,
        deltaMode: event.deltaMode,
      })
      const anchorRatio = (anchor - current.start) / Math.max(1, currentCount)
      let start = Math.round(anchor - clampedCount * anchorRatio)
      start = Math.max(0, Math.min(state.length - clampedCount, start))
      setViewport({ start, end: start + clampedCount })
    }

    element.addEventListener("wheel", onWheel, { passive: false, capture: true })
    return () => element.removeEventListener("wheel", onWheel, { capture: true })
  }, [])

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>): void => {
      event.currentTarget.setPointerCapture(event.pointerId)
      wheelZoomRef.current = true
      dragRef.current = { x: event.clientX, viewport }
    },
    [viewport],
  )

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>): void => {
      const bounds = event.currentTarget.getBoundingClientRect()
      setHover({
        index: indexFromPointer(event.clientX),
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      })
      if (!dragRef.current || length === 0) return
      const barWidth = Math.max(
        1,
        (size.width - leftPadding - rightPadding) /
          (dragRef.current.viewport.end - dragRef.current.viewport.start),
      )
      const shift = Math.round((dragRef.current.x - event.clientX) / barWidth)
      const count = dragRef.current.viewport.end - dragRef.current.viewport.start
      const start = Math.max(0, Math.min(length - count, dragRef.current.viewport.start + shift))
      setViewport({ start, end: start + count })
    },
    [indexFromPointer, leftPadding, length, rightPadding, size.width],
  )

  const handlePointerUp = useCallback((): void => {
    dragRef.current = null
  }, [])

  const handlePointerLeave = useCallback((): void => {
    if (dragRef.current) return
    wheelZoomRef.current = false
    setHover(EMPTY_HOVER)
  }, [])

  return {
    containerRef,
    canvasRef,
    size,
    viewport,
    hoverIndex: hover.index,
    hoverX: hover.x,
    hoverY: hover.y,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerLeave,
  }
}

export function clampViewport(current: ChartViewport, previousLength: number, length: number): ChartViewport {
  if (length === 0) return { start: 0, end: 0 }
  const count = Math.max(1, Math.min(current.end - current.start, length))
  // A viewport pinned to the latest point stays pinned as the timeline grows or shrinks.
  const end = current.end >= previousLength ? length : Math.min(Math.max(current.end, count), length)
  return { start: Math.max(0, end - count), end }
}

function initialViewport(length: number, defaultWindow: number): ChartViewport {
  const windowSize = Math.min(length, Math.max(1, defaultWindow))
  return { start: Math.max(0, length - windowSize), end: length }
}

export const CHART_NAVIGATION_HINT = "Drag to pan · Click, then scroll to zoom"

/** Sizes the canvas backing store for the device pixel ratio. Runs only when the size changes. */
export function useCanvasSize(canvasRef: RefObject<HTMLCanvasElement | null>, size: ChartSize): void {
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.width = Math.floor(size.width * size.dpr)
    canvas.height = Math.floor(size.height * size.dpr)
    canvas.style.width = `${size.width}px`
    canvas.style.height = `${size.height}px`
  }, [canvasRef, size])
}

/** Redraws at most once per animation frame when deps change. Declare after useCanvasSize. */
export function useCanvasFrame(
  canvasRef: RefObject<HTMLCanvasElement | null>,
  size: ChartSize,
  draw: (ctx: CanvasRenderingContext2D) => void,
  deps: DependencyList,
): void {
  const drawRef = useRef(draw)
  drawRef.current = draw
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const canvas = canvasRef.current
      const ctx = canvas?.getContext("2d")
      if (!ctx) return
      ctx.setTransform(size.dpr, 0, 0, size.dpr, 0, 0)
      drawRef.current(ctx)
    })
    return () => cancelAnimationFrame(frame)
  }, [canvasRef, size, ...deps])
}

function currentDpr(): number {
  return typeof window === "undefined" ? 1 : window.devicePixelRatio || 1
}
